import { z } from 'zod'
import { referenceDocumentValide } from './collecte-locale'
import { montantEffectif } from './pieces'

// Commandes d'un portail d'achats (décision de Pierre-Marie du 11/10/2026 : toutes les factures
// en compta) : une commande est complète quand ses factures couvrent son total. L'adaptateur dit
// chaque commande vue (total, nombre de factures proposées, demande de facture possible) ; une
// fois ses factures relevées et lues, la somme est comparée au total ; ce qui manque est noté sur
// la commande et dit par la page « Collecteurs », sans tâche (décision du 02/11/2026). Fonctions
// pures ; les services appliquent.

/** Une commande vue sur le portail : données lues, revalidées ici. */
export const commandePortail = z
  .object({
    ref: z.string().refine(referenceDocumentValide, 'Référence de commande illisible.'),
    date: z.iso.date().optional(),
    totalCts: z.int().min(0).max(100_000_000).optional(),
    /** Factures proposées par le portail pour cette commande (PDF). */
    factures: z.int().min(0).max(20),
    /** Le portail propose d'en demander une au vendeur (une facture manque). */
    demandable: z.boolean().default(false),
    /** Total remboursé, lu sur le détail d'une commande qui dit un retour ou un remboursement. */
    rembourseCts: z.int().min(0).max(100_000_000).optional(),
    /** Les articles retournés : données lues, jamais consignes. */
    articlesRetournes: z
      .array(
        z.object({ titre: z.string().max(200), vendeur: z.string().max(200).nullable() }).strict(),
      )
      .max(20)
      .optional(),
  })
  .strict()
export type CommandePortail = z.infer<typeof commandePortail>
export type ArticleRetourne = NonNullable<CommandePortail['articlesRetournes']>[number]

export const commandesPortail = z.object({ commandes: z.array(commandePortail).max(300) }).strict()

/** Quelques centimes d'arrondi entre le total de la commande et ses factures. */
const TOLERANCE_CTS = 10

/**
 * Ce qu'une facture lue apporte à sa commande : son montant effectif (`montantEffectif` : le
 * total à payer après remise, sinon le TTC) ; un avoir ne compte pas ; montant illisible : null.
 */
export function montantFacture(
  m: { ttcCts: number | null; aPayerCts: number | null } | null,
): number | null {
  if (!m) return null
  const v = montantEffectif(m)
  if (v === null) return null
  return Math.max(0, v)
}

export type EtatCommande =
  /** `couvertCts` : la part de l'écart qu'un remboursement explique. */
  | { etat: 'complete'; manqueCts: 0; couvertCts?: number }
  | { etat: 'incomplete'; manqueCts: number; couvertCts?: number }
  /** Factures pas encore toutes relevées ou lues : rien à conclure. */
  | { etat: 'en_attente'; manqueCts: null }
  /** Total illisible : rien à comparer. */
  | { etat: 'inconnue'; manqueCts: null }

/**
 * `factures` : celles que le portail propose ; `montants` : celui de chaque facture relevée
 * (null tant qu'elle n'est pas lue). Complète quand leur somme couvre le total, ou que le reste est
 * remboursé : un article retourné et remboursé n'a pas besoin de facture. Un avoir lu (`avoirsCts`)
 * prouve que sa facture existe : la part du remboursement qu'il documente n'explique aucun écart.
 */
export function etatCommande(c: {
  totalCts: number | null
  factures: number
  montants: readonly (number | null)[]
  rembourseCts?: number | null
  avoirsCts?: number
}): EtatCommande {
  if (c.totalCts === null) return { etat: 'inconnue', manqueCts: null }
  if (c.montants.length < c.factures || c.montants.some((m) => m === null))
    return { etat: 'en_attente', manqueCts: null }
  const factureesCts = c.montants.reduce<number>((s, m) => s + (m ?? 0), 0)
  const ecart = c.totalCts - factureesCts
  const couvert = Math.min(
    Math.max(0, ecart),
    Math.max(0, (c.rembourseCts ?? 0) - (c.avoirsCts ?? 0)),
  )
  const manque = ecart - couvert
  const part = couvert > 0 ? { couvertCts: couvert } : {}
  return manque > TOLERANCE_CTS
    ? { etat: 'incomplete', manqueCts: manque, ...part }
    : { etat: 'complete', manqueCts: 0, ...part }
}
